import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Bug,
  CheckCircle2,
  CheckSquare,
  CircleDashed,
  CircleDot,
  Clock,
  FileText,
  Filter,
  Flame,
  GitBranch,
  GripVertical,
  Hash,
  Kanban,
  ListTodo,
  Minus,
  Palette,
  Plus,
  Sparkles,
  Tag,
  Target,
  User,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';

import { EmptyState } from '@/components/common/empty-state';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import {
  createStatus,
  getProjectByKey,
  reorderStatuses,
  type StatusCategory,
} from '@/lib/project-api';
import {
  getBoardApi,
  moveBoardIssueApi,
  type BoardCard,
  type BoardColumn,
  type BoardResponse,
} from '@/lib/sprint-api';
import { useAuthStore } from '@/stores/auth-store';
import { useIssueDrawerStore } from '@/stores/issue-drawer-store';
import { cn, initialsFrom } from '@/lib/utils';

type IssueType = 'STORY' | 'TASK' | 'BUG' | 'SUBTASK';

const TYPE_META: Record<
  IssueType,
  { label: string; icon: typeof FileText; iconColor: string; bg: string }
> = {
  STORY: {
    label: 'Story',
    icon: FileText,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
  TASK: {
    label: 'Task',
    icon: CheckSquare,
    iconColor: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10 ring-sky-500/20',
  },
  BUG: {
    label: 'Bug',
    icon: Bug,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 ring-rose-500/20',
  },
  SUBTASK: {
    label: 'Subtask',
    icon: CircleDot,
    iconColor: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-muted ring-border',
  },
};

const PRIORITY_META: Record<
  BoardCard['priority'],
  { label: string; icon: typeof ArrowUp; color: string }
> = {
  LOWEST: { label: 'Lowest', icon: ArrowDown, color: 'text-slate-400' },
  LOW: { label: 'Low', icon: ArrowDown, color: 'text-emerald-500' },
  MEDIUM: { label: 'Medium', icon: Minus, color: 'text-amber-500' },
  HIGH: { label: 'High', icon: ArrowUp, color: 'text-orange-500' },
  HIGHEST: { label: 'Highest', icon: Flame, color: 'text-rose-500' },
};

const CATEGORY_META: Record<
  BoardColumn['category'],
  { label: string; icon: typeof CircleDashed; iconColor: string; chipBg: string }
> = {
  TODO: {
    label: 'To do',
    icon: CircleDashed,
    iconColor: 'text-slate-500 dark:text-slate-400',
    chipBg: 'bg-slate-500/10 ring-slate-500/20',
  },
  IN_PROGRESS: {
    label: 'In progress',
    icon: Clock,
    iconColor: 'text-sky-600 dark:text-sky-400',
    chipBg: 'bg-sky-500/10 ring-sky-500/20',
  },
  DONE: {
    label: 'Done',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    chipBg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
};

// dnd-kit id prefixes — keeping them namespaced lets us distinguish cards from
// column droppables and from the column drag-handles (horizontal reorder).
const CARD_PREFIX = 'card:';
const COL_PREFIX = 'col:';
const COL_HANDLE_PREFIX = 'colh:';

export function ProjectBoardPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();
  const qc = useQueryClient();
  const { error, success } = useToast();
  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === orgId);
  const canManageStatus = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('status.manage_custom'),
  );

  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });

  const boardQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'board'],
    queryFn: () =>
      projectQuery.data
        ? getBoardApi(orgId, projectQuery.data.id)
        : Promise.resolve<BoardResponse | null>(null),
    enabled: Boolean(projectQuery.data),
  });

  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<IssueType[]>([]);
  const [parentFilter, setParentFilter] = useState<string>('ALL');
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [createStatusOpen, setCreateStatusOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const data = boardQuery.data ?? null;

  const assigneeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    data?.columns.forEach((col) =>
      col.issues.forEach((issue) => {
        if (issue.assigneeId && issue.assigneeName) {
          seen.set(issue.assigneeId, issue.assigneeName);
        }
      }),
    );
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [data]);

  const parentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    data?.columns.forEach((col) =>
      col.issues.forEach((issue) => {
        if (issue.parentIssueId && issue.parentIssueKey) {
          seen.set(issue.parentIssueId, issue.parentIssueKey);
        }
      }),
    );
    return Array.from(seen.entries()).map(([id, key]) => ({ id, key }));
  }, [data]);

  const filteredColumns: BoardColumn[] = useMemo(() => {
    if (!data) return [];
    return data.columns.map((col) => ({
      ...col,
      issues: col.issues.filter((issue) => {
        if (assigneeFilter.length && !assigneeFilter.includes(issue.assigneeId ?? '')) return false;
        if (typeFilter.length && !typeFilter.includes(issue.type)) return false;
        if (parentFilter !== 'ALL') {
          if (parentFilter === 'NONE' && issue.parentIssueId) return false;
          if (parentFilter !== 'NONE' && issue.parentIssueId !== parentFilter) return false;
        }
        return true;
      }),
    }));
  }, [data, assigneeFilter, typeFilter, parentFilter]);

  const moveMutation = useMutation({
    mutationFn: (body: Parameters<typeof moveBoardIssueApi>[2]) =>
      moveBoardIssueApi(orgId, projectQuery.data!.id, body),
    onMutate: (body) => {
      const key = ['org', orgId, 'project', projectKey, 'board'] as const;
      const previous = qc.getQueryData<BoardResponse | null>(key);
      if (!previous) return { previous };
      qc.setQueryData(key, applyOptimisticMove(previous, body));
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['org', orgId, 'project', projectKey, 'board'], ctx.previous);
      }
      error('Move failed', extractApiErrorMessage(err));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
    },
  });

  const reorderColumnsMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      reorderStatuses(orgId, projectQuery.data!.id, orderedIds),
    onMutate: (orderedIds) => {
      const key = ['org', orgId, 'project', projectKey, 'board'] as const;
      const previous = qc.getQueryData<BoardResponse | null>(key);
      if (!previous) return { previous };
      const byId = new Map(previous.columns.map((c) => [c.statusId, c]));
      const reordered = orderedIds
        .map((id) => byId.get(id))
        .filter((c): c is BoardColumn => Boolean(c));
      qc.setQueryData(key, { ...previous, columns: reordered });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['org', orgId, 'project', projectKey, 'board'], ctx.previous);
      }
      error('Reorder failed', extractApiErrorMessage(err));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
    },
  });

  const createStatusMutation = useMutation({
    mutationFn: (body: {
      name: string;
      category: StatusCategory;
      colorHex?: string;
      wipLimit?: number | null;
    }) => createStatus(orgId, projectQuery.data!.id, body),
    onSuccess: (created) => {
      success('Column added', created.name);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
      setCreateStatusOpen(false);
    },
    onError: (e) => error('Could not add column', extractApiErrorMessage(e)),
  });

  const handleDragStart = (evt: DragStartEvent) => {
    const id = String(evt.active.id);
    if (id.startsWith(CARD_PREFIX)) setActiveCardId(id.slice(CARD_PREFIX.length));
    else if (id.startsWith(COL_HANDLE_PREFIX))
      setActiveColumnId(id.slice(COL_HANDLE_PREFIX.length));
  };

  const handleDragOver = (evt: DragOverEvent) => {
    if (!evt.over) {
      setOverColumn(null);
      return;
    }
    const overId = String(evt.over.id);
    if (overId.startsWith(COL_PREFIX)) {
      setOverColumn(overId);
      return;
    }
    if (overId.startsWith(CARD_PREFIX) && data) {
      const overIssueId = overId.slice(CARD_PREFIX.length);
      const column = data.columns.find((c) => c.issues.some((i) => i.id === overIssueId));
      if (column) setOverColumn(`${COL_PREFIX}${column.statusId}`);
    }
  };

  const handleDragEnd = (evt: DragEndEvent) => {
    setOverColumn(null);
    setActiveCardId(null);
    setActiveColumnId(null);
    if (!evt.over || !evt.active || !data) return;

    const activeKey = String(evt.active.id);
    const overKey = String(evt.over.id);

    // Column drag (horizontal reorder).
    if (activeKey.startsWith(COL_HANDLE_PREFIX)) {
      if (!overKey.startsWith(COL_HANDLE_PREFIX)) return;
      const fromId = activeKey.slice(COL_HANDLE_PREFIX.length);
      const toId = overKey.slice(COL_HANDLE_PREFIX.length);
      if (fromId === toId) return;
      const ids = data.columns.map((c) => c.statusId);
      const fromIdx = ids.indexOf(fromId);
      const toIdx = ids.indexOf(toId);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = arrayMove(ids, fromIdx, toIdx);
      reorderColumnsMutation.mutate(next);
      return;
    }

    if (!activeKey.startsWith(CARD_PREFIX)) return;
    const issueId = activeKey.slice(CARD_PREFIX.length);

    const fromColumn = data.columns.find((col) => col.issues.some((issue) => issue.id === issueId));
    if (!fromColumn) return;

    // Resolve the target column + optional target card.
    let targetStatusId: string;
    let overIssueId: string | null = null;
    if (overKey.startsWith(COL_PREFIX)) {
      targetStatusId = overKey.slice(COL_PREFIX.length);
    } else if (overKey.startsWith(CARD_PREFIX)) {
      overIssueId = overKey.slice(CARD_PREFIX.length);
      if (overIssueId === issueId) return; // dropped on itself
      const overColumnRow = data.columns.find((col) =>
        col.issues.some((issue) => issue.id === overIssueId),
      );
      if (!overColumnRow) return;
      targetStatusId = overColumnRow.statusId;
    } else {
      return;
    }

    // Compute before/after neighbour ids based on the drop target.
    const targetColumn = data.columns.find((c) => c.statusId === targetStatusId);
    if (!targetColumn) return;

    // Start from the target column's issues minus the moving card (if same-column).
    const siblings = targetColumn.issues.filter((issue) => issue.id !== issueId);

    let beforeIssueId: string | null = null;
    let afterIssueId: string | null = null;

    if (overIssueId) {
      const overIdx = siblings.findIndex((issue) => issue.id === overIssueId);
      if (overIdx >= 0) {
        // Insert right above the over-card (i.e. take its position, push it down).
        beforeIssueId = siblings[overIdx - 1]?.id ?? null;
        afterIssueId = siblings[overIdx]?.id ?? null;
      }
    } else {
      // Column droppable (empty area / bottom of list): append to the end.
      beforeIssueId = siblings[siblings.length - 1]?.id ?? null;
      afterIssueId = null;
    }

    // Skip no-op reorders inside the same column.
    if (
      fromColumn.statusId === targetStatusId &&
      !overIssueId &&
      fromColumn.issues[fromColumn.issues.length - 1]?.id === issueId
    ) {
      return;
    }

    moveMutation.mutate({
      issueId,
      statusId: targetStatusId,
      beforeIssueId,
      afterIssueId,
    });
  };

  if (projectQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6">
        <EmptyState
          title="Could not load project"
          description={extractApiErrorMessage(projectQuery.error)}
        />
      </div>
    );
  }

  if (boardQuery.isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-4 p-6">
        <Skeleton className="h-10 w-1/3" />
        <div className="grid gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      </div>
    );
  }

  if (boardQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6">
        <EmptyState
          title="Could not load board"
          description={extractApiErrorMessage(boardQuery.error)}
          action={<Button onClick={() => boardQuery.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data || !data.activeSprintId) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
        <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
          <div
            className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl"
            aria-hidden
          />
          <div className="relative flex flex-wrap items-start gap-3 p-6">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Kanban className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Board</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Drag cards between columns to change status.
              </p>
            </div>
          </div>
        </div>
        <EmptyState
          icon={Kanban}
          title="No active sprint"
          description="The board shows issues in the active sprint. Start a sprint from the backlog to populate it."
          action={
            <Button asChild>
              <Link to={`/o/${orgSlug}/projects/${projectKey}/backlog`}>Go to backlog</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Aggregate stats for the hero.
  const totalIssues = data.columns.reduce((sum, c) => sum + c.issues.length, 0);
  const doneCount = data.columns
    .filter((c) => c.category === 'DONE')
    .reduce((sum, c) => sum + c.issues.length, 0);
  const totalSP = data.columns.reduce(
    (sum, c) => sum + c.issues.reduce((s, i) => s + (i.storyPoints ?? 0), 0),
    0,
  );
  const progressPct = totalIssues > 0 ? Math.round((doneCount / totalIssues) * 100) : 0;

  const anyFiltersActive =
    assigneeFilter.length > 0 || typeFilter.length > 0 || parentFilter !== 'ALL';

  const toggleAssignee = (id: string) => {
    setAssigneeFilter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const toggleType = (t: IssueType) => {
    setTypeFilter((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const activeCard = activeCardId
    ? data.columns.flatMap((c) => c.issues).find((i) => i.id === activeCardId) ?? null
    : null;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Kanban className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-2xl font-semibold tracking-tight">Board</h1>
                {data.sprint ? (
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    {data.sprint.name}
                  </span>
                ) : null}
              </div>
              {data.sprint?.goal ? (
                <p className="mt-1 flex items-start gap-1.5 text-sm text-muted-foreground">
                  <Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <span className="line-clamp-2">{data.sprint.goal}</span>
                </p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">
                  Drag cards between columns (or within a column) to reorder.
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px border-t bg-border sm:grid-cols-4">
          <BoardStatTile icon={ListTodo} label="Total" value={`${totalIssues}`} />
          <BoardStatTile
            icon={CheckCircle2}
            label="Done"
            value={`${doneCount} / ${totalIssues}`}
            accent="emerald"
          />
          <BoardStatTile icon={Hash} label="Story points" value={`${totalSP} SP`} />
          <BoardStatTile
            icon={Target}
            label="Progress"
            value={`${progressPct}%`}
            {...(progressPct >= 100 ? { accent: 'emerald' as const } : {})}
          />
        </div>
      </div>

      {/* Filters */}
      <Card className="overflow-hidden">
        <CardContent className="space-y-4 p-4">
          {/* Types chips */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Filter className="h-3 w-3" aria-hidden /> Types
            </div>
            <div className="flex flex-wrap gap-2">
              {(['STORY', 'TASK', 'BUG', 'SUBTASK'] as IssueType[]).map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const selected = typeFilter.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    aria-pressed={selected}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                      selected
                        ? `${meta.bg} ring-2`
                        : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-3.5 w-3.5',
                        selected ? meta.iconColor : 'text-muted-foreground',
                      )}
                      aria-hidden
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Assignees chips */}
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <Users className="h-3 w-3" aria-hidden /> Assignees
            </div>
            {assigneeOptions.length === 0 ? (
              <p className="text-xs italic text-muted-foreground">No assignees on board yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {assigneeOptions.map((a) => {
                  const selected = assigneeFilter.includes(a.id);
                  const initial = a.name.trim().charAt(0).toUpperCase() || '?';
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAssignee(a.id)}
                      aria-pressed={selected}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full border px-2 py-1 text-xs font-medium transition-all',
                        selected
                          ? 'border-primary/40 bg-primary/10 text-primary ring-2 ring-primary/30'
                          : 'border-input text-foreground hover:bg-muted/60',
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold uppercase ring-1',
                          selected
                            ? 'bg-primary text-primary-foreground ring-primary'
                            : 'bg-muted text-muted-foreground ring-border',
                        )}
                        aria-hidden
                      >
                        {initial}
                      </span>
                      {a.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Parent + clear */}
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <GitBranch className="h-3 w-3" aria-hidden /> Parent / Epic
              </div>
              <Select
                value={parentFilter}
                onChange={(e) => setParentFilter(e.target.value)}
                wrapperClassName="sm:max-w-xs"
              >
                <option value="ALL">All issues</option>
                <option value="NONE">No parent (top-level)</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.key}
                  </option>
                ))}
              </Select>
            </div>
            {anyFiltersActive ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  setAssigneeFilter([]);
                  setTypeFilter([]);
                  setParentFilter('ALL');
                }}
              >
                <X className="h-3.5 w-3.5" aria-hidden /> Clear filters
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setOverColumn(null);
          setActiveCardId(null);
          setActiveColumnId(null);
        }}
      >
        <SortableContext
          items={filteredColumns.map((c) => `${COL_HANDLE_PREFIX}${c.statusId}`)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredColumns.map((column) => (
              <BoardColumnView
                key={column.statusId}
                column={column}
                orgId={orgId}
                orgSlug={orgSlug}
                projectKey={projectKey}
                over={overColumn === `${COL_PREFIX}${column.statusId}`}
                activeCardId={activeCardId}
                isColumnDragging={activeColumnId === column.statusId}
                canManageStatus={canManageStatus}
              />
            ))}
            {canManageStatus ? (
              <AddColumnTile onClick={() => setCreateStatusOpen(true)} />
            ) : null}
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeCard ? <BoardCardPreview issue={activeCard} /> : null}
        </DragOverlay>
      </DndContext>

      <CreateStatusDialog
        open={createStatusOpen}
        onOpenChange={setCreateStatusOpen}
        pending={createStatusMutation.isPending}
        onSubmit={(body) => createStatusMutation.mutate(body)}
      />
    </div>
  );
}

function BoardStatTile({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Kanban;
  label: string;
  value: string;
  accent?: 'emerald';
}) {
  return (
    <div className="flex items-center gap-3 bg-background px-4 py-3">
      <div
        className={cn(
          'flex h-9 w-9 items-center justify-center rounded-lg ring-1',
          accent === 'emerald'
            ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400'
            : 'bg-primary/5 text-primary ring-primary/20',
        )}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function BoardColumnView({
  column,
  orgId,
  orgSlug,
  projectKey,
  over,
  activeCardId,
  isColumnDragging,
  canManageStatus,
}: {
  column: BoardColumn;
  orgId: string;
  orgSlug: string;
  projectKey: string;
  over: boolean;
  activeCardId: string | null;
  isColumnDragging: boolean;
  canManageStatus: boolean;
}) {
  // Column is itself a sortable item for horizontal reordering. Listeners are
  // attached ONLY to the grip button so card drags don't accidentally move the
  // whole column. The ref + transform apply to the whole column container.
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: `${COL_HANDLE_PREFIX}${column.statusId}`, disabled: !canManageStatus });

  // Inner card drop area — kept separate from the column sortable so dnd-kit
  // can distinguish "drop a card into this column" from "reorder columns".
  const { setNodeRef: setCardDropRef } = useDroppable({
    id: `${COL_PREFIX}${column.statusId}`,
  });

  const wipExceeded = column.wipLimit != null && column.issues.length > column.wipLimit;
  const wipPct = column.wipLimit
    ? Math.min(100, Math.round((column.issues.length / column.wipLimit) * 100))
    : null;
  const catMeta = CATEGORY_META[column.category];
  const CatIcon = catMeta.icon;

  const outerStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isColumnDragging || isSortableDragging ? 0.5 : 1,
  } as const;

  return (
    <div
      ref={setSortableRef}
      {...attributes}
      style={outerStyle}
      className={cn(
        'flex min-h-[26rem] flex-col overflow-hidden rounded-xl border bg-muted/30 shadow-sm transition-all',
        over ? 'bg-primary/5 ring-2 ring-primary/40' : '',
        isSortableDragging ? 'z-10 ring-2 ring-primary/50' : '',
      )}
    >
      {/* Colored top strip using the column's colorHex. */}
      <div
        className="h-1.5 w-full"
        style={{
          background: `linear-gradient(to right, ${column.colorHex}, ${column.colorHex}66 70%, transparent)`,
        }}
        aria-hidden
      />

      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b bg-background/60 px-3 py-2.5 backdrop-blur">
        <div className="flex min-w-0 items-center gap-2">
          {canManageStatus ? (
            <button
              type="button"
              {...listeners}
              className="flex h-6 w-6 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:cursor-grabbing"
              aria-label={`Reorder column ${column.name}`}
              title="Drag to reorder column"
            >
              <GripVertical className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
          <div
            className={cn('flex h-7 w-7 items-center justify-center rounded-lg ring-1', catMeta.chipBg)}
            title={catMeta.label}
          >
            <CatIcon className={cn('h-3.5 w-3.5', catMeta.iconColor)} aria-hidden />
          </div>
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ background: column.colorHex }}
            aria-hidden
          />
          <span className="truncate text-sm font-semibold">{column.name}</span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold tabular-nums ring-1 ring-border">
            {column.issues.length}
          </span>
        </div>
        {column.wipLimit != null ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ring-1',
              wipExceeded
                ? 'bg-rose-500/10 text-rose-600 ring-rose-500/30 dark:text-rose-400'
                : 'bg-muted text-muted-foreground ring-border',
            )}
            title={wipExceeded ? 'WIP limit exceeded' : 'Work-in-progress limit'}
          >
            {column.issues.length}/{column.wipLimit}
          </span>
        ) : null}
      </div>

      {/* Optional WIP bar */}
      {wipPct != null ? (
        <div className="h-0.5 w-full bg-muted">
          <div
            className={cn('h-full transition-all', wipExceeded ? 'bg-rose-500' : 'bg-primary/60')}
            style={{ width: `${wipPct}%` }}
          />
        </div>
      ) : null}

      <div ref={setCardDropRef} className="flex-1 space-y-2 p-2">
        {column.issues.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
              <CatIcon className={cn('h-4 w-4', catMeta.iconColor)} aria-hidden />
            </div>
            <p className="text-xs text-muted-foreground">Drop cards here</p>
          </div>
        ) : (
          <SortableContext
            items={column.issues.map((issue) => `${CARD_PREFIX}${issue.id}`)}
            strategy={verticalListSortingStrategy}
          >
            {column.issues.map((issue) => (
              <BoardCardView
                key={issue.id}
                issue={issue}
                colorHex={column.colorHex}
                orgId={orgId}
                orgSlug={orgSlug}
                projectKey={projectKey}
                isOverlayActive={activeCardId === issue.id}
              />
            ))}
          </SortableContext>
        )}
      </div>
    </div>
  );
}

function AddColumnTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-h-[26rem] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-border bg-muted/10 p-6 text-muted-foreground transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 hover:text-primary hover:shadow-md"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-background ring-1 ring-border transition-colors group-hover:bg-primary/10 group-hover:text-primary group-hover:ring-primary/30">
        <Plus className="h-5 w-5" aria-hidden />
      </div>
      <div className="text-center">
        <div className="text-sm font-semibold">Add column</div>
        <div className="mt-0.5 text-xs">Create a new status for this board</div>
      </div>
    </button>
  );
}

function BoardCardView({
  issue,
  colorHex,
  orgId,
  orgSlug,
  projectKey,
  isOverlayActive,
}: {
  issue: BoardCard;
  colorHex: string;
  orgId: string;
  orgSlug: string;
  projectKey: string;
  isOverlayActive: boolean;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  const { attributes, listeners, setNodeRef, isDragging, transform, transition } = useSortable({
    id: `${CARD_PREFIX}${issue.id}`,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => openIssue({ orgId, orgSlug, projectKey, issueKey: issue.key })}
      className={cn(
        'group relative overflow-hidden rounded-lg border bg-background text-sm shadow-sm transition-all',
        isOverlayActive
          ? 'border-dashed bg-muted/40 opacity-40'
          : 'cursor-grab hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md active:cursor-grabbing',
        isDragging ? 'ring-2 ring-primary' : '',
      )}
      aria-hidden={isOverlayActive}
    >
      {/* Left color rail picking up the column's status color. */}
      <span
        className="absolute inset-y-0 left-0 w-1"
        style={{ background: colorHex }}
        aria-hidden
      />
      <div className="space-y-2 px-3 py-2.5 pl-4">
        <BoardCardInner
          issue={issue}
          orgId={orgId}
          orgSlug={orgSlug}
          projectKey={projectKey}
          interactive
        />
      </div>
    </div>
  );
}

function BoardCardPreview({ issue }: { issue: BoardCard }) {
  return (
    <div
      className="rounded-lg border-2 border-primary/60 bg-background text-sm shadow-2xl ring-4 ring-primary/10"
      style={{ cursor: 'grabbing' }}
    >
      <div className="space-y-2 px-3 py-2.5">
        <BoardCardInner issue={issue} />
      </div>
    </div>
  );
}

function BoardCardInner({
  issue,
  orgId,
  orgSlug,
  projectKey,
  interactive,
}: {
  issue: BoardCard;
  orgId?: string | undefined;
  orgSlug?: string | undefined;
  projectKey?: string | undefined;
  interactive?: boolean | undefined;
}) {
  const typeMeta = TYPE_META[issue.type];
  const TypeIconCmp = typeMeta.icon;
  const priMeta = PRIORITY_META[issue.priority];
  const PriIconCmp = priMeta.icon;
  const initials = issue.assigneeName
    ? initialsFrom(issue.assigneeName, issue.assigneeEmail ?? undefined)
    : '';

  return (
    <>
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-md ring-1',
            typeMeta.bg,
          )}
          title={typeMeta.label}
        >
          <TypeIconCmp className={cn('h-3 w-3', typeMeta.iconColor)} aria-hidden />
        </div>
        {interactive && orgId && orgSlug && projectKey ? (
          <BoardIssueKeyButton
            issueKey={issue.key}
            orgId={orgId}
            orgSlug={orgSlug}
            projectKey={projectKey}
          />
        ) : (
          <span className="shrink-0 font-mono text-[11px] font-semibold text-primary">
            {issue.key}
          </span>
        )}
        {issue.parentIssueKey ? (
          <span
            className="ml-auto inline-flex items-center gap-0.5 rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-muted-foreground ring-1 ring-border"
            title={`Parent: ${issue.parentIssueKey}`}
          >
            <GitBranch className="h-2.5 w-2.5" aria-hidden />
            {issue.parentIssueKey}
          </span>
        ) : null}
        <PriIconCmp
          className={cn('ml-auto h-4 w-4 shrink-0', priMeta.color, issue.parentIssueKey ? 'ml-1.5' : '')}
          aria-label={`Priority ${priMeta.label}`}
        />
      </div>

      <p className="line-clamp-2 text-sm font-medium text-foreground">{issue.title}</p>

      <div className="flex items-center justify-between gap-2">
        {issue.assigneeName ? (
          <span
            className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground"
            title={issue.assigneeName}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
              {initials}
            </span>
            <span className="max-w-[7rem] truncate">{issue.assigneeName}</span>
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] italic text-muted-foreground">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border">
              <User className="h-3 w-3" aria-hidden />
            </span>
            Unassigned
          </span>
        )}
        {issue.storyPoints != null ? (
          <span className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full bg-muted px-2 text-[11px] font-semibold tabular-nums ring-1 ring-border">
            {issue.storyPoints}
          </span>
        ) : null}
      </div>
    </>
  );
}

function BoardIssueKeyButton({
  issueKey,
  orgId,
  orgSlug,
  projectKey,
}: {
  issueKey: string;
  orgId: string;
  orgSlug: string;
  projectKey: string;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        openIssue({ orgId, orgSlug, projectKey, issueKey });
      }}
      className="shrink-0 font-mono text-[11px] font-semibold text-primary hover:underline"
    >
      {issueKey}
    </button>
  );
}

// -------------------- Helpers --------------------

interface OptimisticMove {
  issueId: string;
  statusId: string;
  beforeIssueId?: string | null;
  afterIssueId?: string | null;
}

/**
 * Apply a move to the cached board data:
 *  - If the issue is moving across columns, strip it from the source column and insert it
 *    into the destination column at the position implied by `beforeIssueId` / `afterIssueId`.
 *  - If it's a within-column reorder, remove + re-insert at the requested position.
 * Exported via module internals for unit testing the reorder math is not required here
 * because the server owns the authoritative boardOrder; the UI simply reflects the move.
 */
export function applyOptimisticMove(data: BoardResponse, move: OptimisticMove): BoardResponse {
  let moved: BoardCard | null = null;

  const columnsMinusMoved = data.columns.map((col) => ({
    ...col,
    issues: col.issues.filter((issue) => {
      if (issue.id === move.issueId) {
        moved = issue;
        return false;
      }
      return true;
    }),
  }));

  if (!moved) return data;

  const nextColumns = columnsMinusMoved.map((col) => {
    if (col.statusId !== move.statusId) return col;
    const insertAt = resolveInsertIndex(col.issues, move.beforeIssueId, move.afterIssueId);
    const next = col.issues.slice();
    next.splice(insertAt, 0, moved as BoardCard);
    return { ...col, issues: next };
  });

  return { ...data, columns: nextColumns };
}

function resolveInsertIndex(
  siblings: BoardCard[],
  beforeIssueId: string | null | undefined,
  afterIssueId: string | null | undefined,
): number {
  if (afterIssueId) {
    const afterIdx = siblings.findIndex((i) => i.id === afterIssueId);
    if (afterIdx >= 0) return afterIdx;
  }
  if (beforeIssueId) {
    const beforeIdx = siblings.findIndex((i) => i.id === beforeIssueId);
    if (beforeIdx >= 0) return beforeIdx + 1;
  }
  return siblings.length;
}

// -------------------- Create status / column dialog --------------------

const STATUS_CATEGORIES: Array<{
  id: StatusCategory;
  label: string;
  icon: typeof CircleDashed;
  iconColor: string;
  chipBg: string;
  ring: string;
}> = [
  {
    id: 'TODO',
    label: 'To do',
    icon: CircleDashed,
    iconColor: 'text-slate-500 dark:text-slate-400',
    chipBg: 'bg-slate-500/10',
    ring: 'ring-slate-500/50 border-slate-500/50',
  },
  {
    id: 'IN_PROGRESS',
    label: 'In progress',
    icon: Clock,
    iconColor: 'text-sky-600 dark:text-sky-400',
    chipBg: 'bg-sky-500/10',
    ring: 'ring-sky-500/50 border-sky-500/50',
  },
  {
    id: 'DONE',
    label: 'Done',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    chipBg: 'bg-emerald-500/10',
    ring: 'ring-emerald-500/50 border-emerald-500/50',
  },
];

const COLOR_PALETTE: string[] = [
  '#64748B', // slate
  '#0EA5E9', // sky
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#F43F5E', // rose
  '#F97316', // orange
  '#F59E0B', // amber
  '#10B981', // emerald
  '#14B8A6', // teal
];

function CreateStatusDialog({
  open,
  onOpenChange,
  pending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pending: boolean;
  onSubmit: (body: {
    name: string;
    category: StatusCategory;
    colorHex?: string;
    wipLimit?: number | null;
  }) => void;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<StatusCategory>('TODO');
  const [color, setColor] = useState<string>(COLOR_PALETTE[0] ?? '#64748B');
  const [wip, setWip] = useState<string>('');

  const reset = () => {
    setName('');
    setCategory('TODO');
    setColor(COLOR_PALETTE[0] ?? '#64748B');
    setWip('');
  };

  const bumpWip = (delta: number) => {
    const current = wip.trim() === '' ? 0 : Number(wip);
    const safe = Number.isFinite(current) ? current : 0;
    const next = Math.min(100, Math.max(0, safe + delta));
    setWip(next === 0 ? '' : String(next));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pending || !name.trim()) return;
    const wipVal = wip.trim() === '' ? null : Number(wip);
    onSubmit({
      name: name.trim(),
      category,
      colorHex: color,
      ...(wipVal && wipVal > 0 ? { wipLimit: wipVal } : {}),
    });
  };

  const validHex = /^#[0-9A-Fa-f]{6}$/.test(color);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Kanban className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Add board column</DialogTitle>
                <DialogDescription className="mt-1 text-xs">
                  Columns map to issue statuses and stay in sync with every view of this project.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form className="space-y-5 px-6 pb-6 pt-5" onSubmit={submit} noValidate>
          {/* Name */}
          <div className="space-y-2">
            <Label
              htmlFor="st-name"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Tag className="h-3.5 w-3.5" aria-hidden /> Name
            </Label>
            <div className="relative">
              <span
                className="pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full"
                style={{ background: validHex ? color : '#94a3b8' }}
                aria-hidden
              />
              <Input
                id="st-name"
                required
                autoFocus
                maxLength={40}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="In review"
                className="h-11 pl-9 text-sm"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                {name.length}/40
              </span>
            </div>
          </div>

          {/* Category chips */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Filter className="h-3.5 w-3.5" aria-hidden /> Category
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {STATUS_CATEGORIES.map((c) => {
                const Icon = c.icon;
                const selected = c.id === category;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    aria-pressed={selected}
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-lg border bg-background px-2 py-2.5 text-sm font-medium transition-all',
                      selected
                        ? cn(c.chipBg, c.ring, 'ring-2 shadow-sm')
                        : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    )}
                  >
                    <Icon
                      className={cn('h-4 w-4', selected ? c.iconColor : 'text-muted-foreground')}
                      aria-hidden
                    />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color palette */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Palette className="h-3.5 w-3.5" aria-hidden /> Color
            </Label>
            <div className="flex flex-wrap items-center gap-2">
              {COLOR_PALETTE.map((hex) => {
                const selected = color.toLowerCase() === hex.toLowerCase();
                return (
                  <button
                    key={hex}
                    type="button"
                    onClick={() => setColor(hex)}
                    aria-pressed={selected}
                    aria-label={`Select color ${hex}`}
                    title={hex}
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-full ring-offset-background transition-all',
                      selected
                        ? 'ring-2 ring-primary ring-offset-2 scale-110'
                        : 'ring-1 ring-border hover:scale-105',
                    )}
                    style={{ background: hex }}
                  >
                    {selected ? (
                      <CheckCircle2 className="h-4 w-4 text-white drop-shadow" aria-hidden />
                    ) : null}
                  </button>
                );
              })}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">or</span>
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#64748B"
                  maxLength={7}
                  className="h-9 w-24 font-mono text-xs uppercase"
                />
              </div>
            </div>
            {!validHex ? (
              <p className="text-[11px] text-rose-500">Color must be a 6-digit hex, e.g. #64748B.</p>
            ) : null}
          </div>

          {/* WIP limit */}
          <div className="space-y-2">
            <Label
              htmlFor="st-wip"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Hash className="h-3.5 w-3.5" aria-hidden /> WIP limit
              <span className="ml-1 font-normal normal-case text-muted-foreground/80">
                (optional)
              </span>
            </Label>
            <div className="flex h-10 items-stretch overflow-hidden rounded-lg border">
              <button
                type="button"
                onClick={() => bumpWip(-1)}
                className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Decrease WIP"
              >
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <input
                id="st-wip"
                type="number"
                min={0}
                max={100}
                value={wip}
                onChange={(e) => setWip(e.target.value)}
                placeholder="No limit"
                className="w-full min-w-0 border-x border-input bg-background text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
              />
              <button
                type="button"
                onClick={() => bumpWip(1)}
                className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Increase WIP"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Cards exceeding the limit show a warning badge on the board.
            </p>
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={pending || !name.trim() || !validHex}
              className="gap-1.5"
            >
              {pending ? (
                <>
                  <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Adding…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" aria-hidden /> Add column
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
